import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { translate, translateObject, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { EnumErrorStates, ErrorObject, TabObject } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import {
  DEFAULT_PROJECT_COLOR_PALETTE,
  Project,
  ProjectService,
  PROJECT_COLOR_LIMIT,
} from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';
import { Utils } from 'app/utils/utils';
import { generalNameValidator } from '../../../../utils/customValidators';
import { ProjectSentimentRangeComponent } from '../project-sentiment-range/project-sentiment-range.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-project-options-drawer',
  templateUrl: './project-options-drawer.component.html',
  styleUrls: ['./project-options-drawer.component.scss'],
  standalone: false,
})
export class ProjectOptionsDrawerComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() project: Project;
  @Input() drawerOpen = true;

  @Output() close = new EventEmitter();

  @ViewChild(ProjectSentimentRangeComponent) rangeComponent: ProjectSentimentRangeComponent;

  displayedColumns = ['lang', 'configName', 'tags', 'createdBy', 'lastUsed'];

  configurations: SemantriaConfiguration[] = [];
  filteredConfigs: SemantriaConfiguration[] = [];
  configProjectMap: { [key: string]: string[] };
  configCreatorMap: Map<string, string> = new Map<string, string>();

  name: string;
  projectNames: string[];
  projectNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  description: string;
  icon: string;
  colorPalette: string[];
  addColorTooltip: string;
  canResetColorPalette;
  hasColorChanges;
  PROJECT_COLOR_LIMIT = PROJECT_COLOR_LIMIT;

  showImportColorPaletteDialog;

  isLoading = { project: true, users: true, saveEdit: false, usersAdd: false };
  isReadOnly = false;
  isAdmin = false;

  activeTab = 'general';
  tabs: TabObject[];

  private accountSub$: Subscription;

  constructor(
    private projectService: ProjectService,
    private accountService: StorageAccountService,
    private groupService: GroupService,
    private configService: ConfigurationService,
    private alertService: AlertService,
    private authService: AuthService,
    private userService: SemantriaUsersService,
    private dialogService: DialogService,
    private systemService: SystemService,
    private translocoService: TranslocoService
  ) {}

  async ngOnInit() {
    AppService.hideBodyOverflow();

    this.isReadOnly = this.authService.isReadOnly;
    this.isAdmin = this.authService.isAccountAdmin;
    const t = this.translocoService.translateObject('project-options');
    this.tabs = [
      { id: 'general', title: t['general'] },
      { id: 'configurations', title: t['configurations'] },
      { id: 'adjust-sentiment-ranges', title: t['adjust-sentiment-ranges'] },
      { id: 'color-palette', title: t['set-color-palette'] },
    ];

    this.name = this.project.name;
    this.description = this.project.description;
    this.icon = this.project.iconUrl;
    this.colorPalette = [...(this.project.colorPalette ?? DEFAULT_PROJECT_COLOR_PALETTE)];
    this.checkColorPalette();

    this.setProjectNames();
    this.configCreatorMap.set('API', 'API');

    try {
      this.configurations = await this.configService.getConfigurations(
        true,
        this.groupService.getSelectedGroupId()
      );
    } catch (err) {
      this.alertService.showErrorToast(translate('project-options.error-fetching-configs'), err);
      return;
    }

    this.accountSub$ = this.accountService.accountSub.subscribe((accountMetadata) => {
      this.configProjectMap = accountMetadata.configurationMap;

      this.filteredConfigs = this.filterConfigurations(this.configurations);
    });

    this.configurations.forEach((config) => {
      this.configCreatorMap.set(config.creator_user_id, '');
    });

    for (const userId of Array.from(this.configCreatorMap.keys()).filter((id) => id !== 'API')) {
      this.userService.getDisplayNameForUser(userId).then((res) => {
        this.configCreatorMap.set(userId, res);
      });
    }

    this.isLoading.saveEdit = false;
  }

  ngOnDestroy(): void {
    if (this.accountSub$) {
      this.accountSub$.unsubscribe();
    }
    AppService.showBodyOverflow();
  }

  async setProjectNames(): Promise<void> {
    this.projectNames = [];
    const projectList = await this.projectService.getProjects();
    for (const project of projectList.items) {
      if (project.id !== this.project.id) {
        this.projectNames.push(project.name);
      }
    }
  }

  filterConfigurations(configs: SemantriaConfiguration[]) {
    return configs.filter((c) => {
      if (!this.configProjectMap) {
        return true;
      }

      return this.configProjectMap[c.id]?.includes(this.project.id);
    });
  }

  onAddColor() {
    this.colorPalette.push(DEFAULT_PROJECT_COLOR_PALETTE[0]);
    this.checkColorPalette();
  }

  onColorChanged(index, color) {
    this.colorPalette.splice(index, 1, color);
    this.checkColorPalette();
  }

  onColorDeleted(index) {
    this.colorPalette.splice(index, 1);
    this.checkColorPalette();
  }

  resetColorPalette() {
    this.colorPalette = [...DEFAULT_PROJECT_COLOR_PALETTE];
    this.checkColorPalette();
  }

  private checkColorPalette() {
    this.canResetColorPalette = !Utils.deepEquals(this.colorPalette, DEFAULT_PROJECT_COLOR_PALETTE);
    this.hasColorChanges = !Utils.deepEquals(
      this.colorPalette,
      this.project.colorPalette ?? DEFAULT_PROJECT_COLOR_PALETTE
    );
    this.addColorTooltip = translate('project-options.add-color');
    if (this.colorPalette.length >= PROJECT_COLOR_LIMIT) {
      this.addColorTooltip = translate('project-options.color-palette-limit');
    }
  }

  onCopyColorPalette() {
    this.systemService.copyToClipboard(this.colorPalette.toString());
    this.alertService.showToast(translate('project-options.colors-copied'));
  }

  onImportColorPaletteModalClosed($event) {
    this.showImportColorPaletteDialog = false;
    if ($event) {
      this.colorPalette = $event;
      this.checkColorPalette();
    }
  }

  cancel() {
    if (this.hasGeneralChanges || this.rangeComponent.hasChanges) {
      this.showDiscardChangesDialog();
      return;
    }
    this.close.emit();
  }

  public async showDiscardChangesDialog(): Promise<void> {
    const translations = translateObject('project-options.discard-name');

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.green,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.close.emit();
        }
      });
  }

  async save() {
    try {
      if (this.hasGeneralChanges) {
        this.isLoading.saveEdit = true;

        const originalName = this.project.name;
        this.project = await this.projectService.updateProject(this.project.id, {
          name: this.name,
          description: this.description,
          iconUrl: this.icon,
          iconSource: 'standard',
        });

        this.projectNames.splice(this.projectNames.indexOf(originalName), 1, this.project.name);
        this.alertService.showToast(translate('project-options.project-settings-updated'));
      }

      if (this.rangeComponent.hasChanges) {
        this.isLoading.saveEdit = true;

        const polarities = this.rangeComponent.save();

        await this.projectService.updateProject(this.project.id, { polarities });
        this.project.polarities = JSON.parse(JSON.stringify(polarities));
        this.alertService.showToast(translate('project-options.sentiment-ranges-set'));
      }

      if (this.hasColorChanges) {
        this.isLoading.saveEdit = true;

        this.project = await this.projectService.updateProject(this.project.id, {
          colorPalette: this.colorPalette,
        });
        this.checkColorPalette();

        this.alertService.showToast(translate('project-options.project-colors-updated'));
      }
    } catch (error) {
      this.alertService.showErrorToast(translate('project-options.project-update-failed'), error);
    } finally {
      this.isLoading.saveEdit = false;
    }
  }

  get hasGeneralChanges() {
    return (
      this.name !== this.project.name ||
      this.description !== this.project.description ||
      this.icon !== this.project.iconUrl
    );
  }

  get canSave() {
    if (this.isLoading.saveEdit) {
      return false;
    }
    if (this.projectNameStatus.state !== EnumErrorStates.SUCCESS) {
      return false;
    }
    if (this.isReadOnly) {
      return false;
    }
    return this.hasGeneralChanges || this.hasColorChanges || this.rangeComponent?.hasChanges;
  }

  onValidateProjectName() {
    generalNameValidator(
      this.name,
      this.projectNameStatus,
      translate('project-options.project'),
      64,
      true,
      this.projectNames,
      false,
      [{ character: '"', name: translate('project-options.double-quotes') }]
    );
  }

  trackColors(index) {
    return index;
  }
}
